import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import PageLayout from '../../components/layout/page-layout'
import { Container } from '../../components/ui/container'
import { FormContainer, FormInput, FormSelect, FormTextarea } from '../../components/forms'
import { Button } from '../../components/ui/button'
import { personnelService, type EmployeeProfile, userService } from '../../services'
import { useAuth } from '../../auth/auth.context'

type CoreForm = {
  firstName: string
  lastName: string
  phoneNumber: string
  email: string
  startDate: string
  function: '' | 'IT' | 'HR' | 'Lager' | 'Putzkraft' | 'Pflege'
}

type WizardDoc = { id: string; file: File; category: string }

const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024

function StepPills({
  activeIndex,
  steps,
}: {
  activeIndex: number
  steps: readonly { key: string; title: string }[]
}) {
  return (
    <div className='flex flex-wrap gap-2'>
      {steps.map((s, i) => (
        <div
          key={s.key}
          className={`px-3 py-1 rounded-full text-xs font-medium border ${
            i === activeIndex
              ? 'bg-blue-50 border-blue-200 text-blue-700'
              : 'bg-white border-gray-200 text-gray-600'
          }`}
        >
          {i + 1}. {s.title}
        </div>
      ))}
    </div>
  )
}

export default function CreateEmployeeWizardPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const steps = useMemo(
    () =>
      [
        { key: 'core', title: t('users.stepCore') },
        { key: 'contact', title: t('users.stepContact') },
        { key: 'address', title: t('users.stepAddress') },
        { key: 'documents', title: t('users.stepDocuments') },
        { key: 'review', title: t('users.stepReview') },
      ] as const,
    [t]
  )

  const [stepIndex, setStepIndex] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  const [core, setCore] = useState<CoreForm>({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    startDate: '',
    function: '',
  })

  const [extra, setExtra] = useState<Partial<EmployeeProfile>>({
    salutation: '',
    title: '',
    workEmail: '',
    privateEmail: '',
    phoneWork: '',
    phoneMobile: '',
    phonePrivate: '',
    addressStreet: '',
    addressPostalCode: '',
    addressCity: '',
    addressCountry: '',
    notes: '',
  })

  const [docs, setDocs] = useState<WizardDoc[]>([])
  const [docCategory, setDocCategory] = useState<string>('CV')

  const functionOptions = useMemo(
    () => [
      { value: '', label: t('common.noSelection') },
      { value: 'IT', label: t('employee.functions.it') },
      { value: 'HR', label: t('employee.functions.hr') },
      { value: 'Lager', label: t('employee.functions.warehouse') },
      { value: 'Putzkraft', label: t('employee.functions.cleaning') },
      { value: 'Pflege', label: t('employee.functions.care') },
    ],
    [t]
  )

  const documentCategoryOptions = useMemo(
    () => [
      { value: 'CV', label: t('employee.documentCategories.cv') },
      { value: 'CONTRACT', label: t('employee.documentCategories.contract') },
      { value: 'OTHER', label: t('employee.documentCategories.other') },
    ],
    [t]
  )

  const createUserMutation = useMutation({
    mutationFn: async () => {
      const payload: any = {
        firstName: core.firstName.trim(),
        lastName: core.lastName.trim(),
        phoneNumber: core.phoneNumber.trim() || undefined,
        role: 'EMPLOYEE',
        companyId: user?.companyId,
        isActive: true,
        startDate: core.startDate || undefined,
        function: core.function || undefined,
        email: core.email?.trim() || undefined,
      }
      const created = await userService.createUser(payload)
      const createdId = created?.id
      if (!createdId) throw new Error('User ID missing')

      await personnelService.upsertEmployeeProfile(createdId, {
        salutation: extra.salutation,
        title: extra.title,
        workEmail: extra.workEmail?.trim() || core.email.trim() || undefined,
        privateEmail: extra.privateEmail,
        phoneWork: extra.phoneWork,
        phoneMobile: extra.phoneMobile?.trim() || core.phoneNumber.trim() || undefined,
        phonePrivate: extra.phonePrivate,
        addressStreet: extra.addressStreet,
        addressPostalCode: extra.addressPostalCode,
        addressCity: extra.addressCity,
        addressCountry: extra.addressCountry,
        notes: extra.notes,
      })

      const groups = new Map<string, File[]>()
      for (const d of docs) {
        const list = groups.get(d.category) || []
        list.push(d.file)
        groups.set(d.category, list)
      }
      for (const [category, files] of groups.entries()) {
        if (files.length === 0) continue
        await personnelService.uploadEmployeeFiles({
          userId: createdId,
          category,
          files,
        })
      }

      return created
    },
    onSuccess: created => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      const createdId = created?.id
      if (!createdId) {
        navigate('/users')
        return
      }
      navigate(`/users/${createdId}/personnel-file`)
    },
    onError: (e: any) => {
      setError(e?.response?.data?.message ?? e?.userMessage ?? t('errors.createEmployee'))
    },
  })

  const validateStep = (idx: number) => {
    if (idx === 0) {
      if (!core.firstName.trim() || !core.lastName.trim()) return t('errors.nameRequired')
      const hasEmail = !!core.email.trim()
      const hasPhone = !!core.phoneNumber.trim()
      if (!hasEmail && !hasPhone) return t('users.loginIdentityRequired')
    }
    return null
  }

  const goNext = () => {
    const msg = validateStep(stepIndex)
    if (msg) {
      setError(msg)
      return
    }
    setError(null)
    setStepIndex(i => Math.min(steps.length - 1, i + 1))
  }

  const goBack = () => {
    setError(null)
    setStepIndex(i => Math.max(0, i - 1))
  }

  const onUpload = async (files: FileList | null) => {
    setError(null)
    if (!files || files.length === 0) return
    setIsUploading(true)
    try {
      for (const file of Array.from(files)) {
        if (file.size > MAX_FILE_SIZE_BYTES) {
          throw new Error(t('errors.fileTooLargeNamed', { name: file.name }))
        }
        setDocs(prev => [
          {
            id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
            file,
            category: docCategory,
          },
          ...prev,
        ])
      }
    } catch (e: any) {
      setError(e?.message || t('common.uploadFailed'))
    } finally {
      setIsUploading(false)
    }
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (stepIndex < steps.length - 1) {
      goNext()
      return
    }
    setError(null)
    createUserMutation.mutate()
  }

  const categoryLabel = (value: string) =>
    documentCategoryOptions.find(o => o.value === value)?.label ?? value

  return (
    <PageLayout
      headerProps={{
        title: t('users.createWizardTitle'),
        subtitle: t('users.createWizardSubtitle'),
        variant: 'dashboard',
      }}
      isLoading={createUserMutation.isPending}
      error={createUserMutation.error}
      loadingMessage={t('users.creatingEmployee')}
      errorMessage={error || t('errors.createEmployee')}
    >
      <Container>
        <FormContainer
          title={t('users.formTitle')}
          subtitle={t('users.formSubtitle')}
          onSubmit={onSubmit}
        >
          <div className='space-y-6'>
            <StepPills activeIndex={stepIndex} steps={steps} />

            {error && (
              <div className='p-3 bg-red-50 border border-red-200 rounded-lg text-red-700'>
                {error}
              </div>
            )}

            {stepIndex === 0 && (
              <div className='space-y-6'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  <FormInput
                    label={t('common.firstName')}
                    name='firstName'
                    value={core.firstName}
                    onChange={e => setCore(prev => ({ ...prev, firstName: e.target.value }))}
                    required
                  />
                  <FormInput
                    label={t('common.lastName')}
                    name='lastName'
                    value={core.lastName}
                    onChange={e => setCore(prev => ({ ...prev, lastName: e.target.value }))}
                    required
                  />
                </div>

                <FormInput
                  label={t('common.phone')}
                  name='phoneNumber'
                  value={core.phoneNumber}
                  onChange={e => setCore(prev => ({ ...prev, phoneNumber: e.target.value }))}
                  placeholder={t('users.phonePlaceholder')}
                  helperText={t('users.loginIdentityHelper')}
                />

                <FormInput
                  label={t('common.email')}
                  name='email'
                  type='email'
                  value={core.email}
                  onChange={e => setCore(prev => ({ ...prev, email: e.target.value }))}
                  placeholder={t('users.emailPlaceholder')}
                />

                <p className='text-sm text-gray-600 -mt-2'>{t('users.loginIdentityHint')}</p>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  <FormInput
                    label={t('common.startDate')}
                    name='startDate'
                    type='date'
                    value={core.startDate}
                    onChange={e => setCore(prev => ({ ...prev, startDate: e.target.value }))}
                  />
                  <FormSelect
                    label={t('common.function')}
                    name='function'
                    value={core.function}
                    onChange={e => setCore(prev => ({ ...prev, function: e.target.value as any }))}
                    options={functionOptions}
                  />
                </div>
              </div>
            )}

            {stepIndex === 1 && (
              <div className='space-y-6'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  <FormInput
                    label={t('common.salutation')}
                    name='salutation'
                    value={extra.salutation || ''}
                    onChange={e => setExtra(prev => ({ ...prev, salutation: e.target.value }))}
                    placeholder={t('users.salutationPlaceholder')}
                  />
                  <FormInput
                    label={t('common.titleField')}
                    name='title'
                    value={extra.title || ''}
                    onChange={e => setExtra(prev => ({ ...prev, title: e.target.value }))}
                    placeholder={t('users.titlePlaceholder')}
                  />
                </div>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  <FormInput
                    label={t('common.workEmail')}
                    name='workEmail'
                    type='email'
                    value={extra.workEmail || ''}
                    onChange={e => setExtra(prev => ({ ...prev, workEmail: e.target.value }))}
                    placeholder={t('users.workEmailPlaceholder')}
                  />
                  <FormInput
                    label={t('common.privateEmail')}
                    name='privateEmail'
                    type='email'
                    value={extra.privateEmail || ''}
                    onChange={e => setExtra(prev => ({ ...prev, privateEmail: e.target.value }))}
                    placeholder={t('users.privateEmailPlaceholder')}
                  />
                </div>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  <FormInput
                    label={t('common.phoneWork')}
                    name='phoneWork'
                    value={extra.phoneWork || ''}
                    onChange={e => setExtra(prev => ({ ...prev, phoneWork: e.target.value }))}
                    placeholder={t('users.phonePlaceholder')}
                  />
                  <FormInput
                    label={t('common.phoneMobile')}
                    name='phoneMobile'
                    value={extra.phoneMobile || ''}
                    onChange={e => setExtra(prev => ({ ...prev, phoneMobile: e.target.value }))}
                    placeholder={t('users.phonePlaceholder')}
                  />
                  <FormInput
                    label={t('common.phonePrivate')}
                    name='phonePrivate'
                    value={extra.phonePrivate || ''}
                    onChange={e => setExtra(prev => ({ ...prev, phonePrivate: e.target.value }))}
                    placeholder={t('users.phonePlaceholder')}
                  />
                </div>
              </div>
            )}

            {stepIndex === 2 && (
              <div className='space-y-6'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  <FormInput
                    label={t('common.street')}
                    name='addressStreet'
                    value={extra.addressStreet || ''}
                    onChange={e => setExtra(prev => ({ ...prev, addressStreet: e.target.value }))}
                    placeholder={t('users.streetPlaceholder')}
                  />
                  <FormInput
                    label={t('common.postalCode')}
                    name='addressPostalCode'
                    value={extra.addressPostalCode || ''}
                    onChange={e =>
                      setExtra(prev => ({ ...prev, addressPostalCode: e.target.value }))
                    }
                    placeholder={t('users.postalCodePlaceholder')}
                  />
                  <FormInput
                    label={t('common.city')}
                    name='addressCity'
                    value={extra.addressCity || ''}
                    onChange={e => setExtra(prev => ({ ...prev, addressCity: e.target.value }))}
                    placeholder={t('users.cityPlaceholder')}
                  />
                  <FormInput
                    label={t('common.country')}
                    name='addressCountry'
                    value={extra.addressCountry || ''}
                    onChange={e => setExtra(prev => ({ ...prev, addressCountry: e.target.value }))}
                    placeholder={t('users.countryPlaceholder')}
                  />
                </div>
              </div>
            )}

            {stepIndex === 3 && (
              <div className='space-y-6'>
                <div className='flex flex-col md:flex-row gap-3 md:items-end'>
                  <div className='flex-1'>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      {t('common.documentCategory')}
                    </label>
                    <select
                      value={docCategory}
                      onChange={e => setDocCategory(e.target.value)}
                      className='w-full px-4 py-3 border border-gray-300 bg-white text-gray-900 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200'
                    >
                      {documentCategoryOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className='inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-200 bg-blue-600 text-white px-4 py-2 cursor-pointer'>
                      {isUploading ? t('employee.uploadingFile') : t('employee.uploadFile')}
                      <input
                        type='file'
                        multiple
                        className='hidden'
                        onChange={e => onUpload(e.target.files)}
                        disabled={isUploading}
                      />
                    </label>
                    <div className='text-xs text-gray-500 mt-1'>{t('employee.maxFileSize')}</div>
                  </div>
                </div>

                {docs.length === 0 ? (
                  <div className='text-sm text-gray-600'>{t('employee.noDocumentsYet')}</div>
                ) : (
                  <div className='divide-y divide-gray-200'>
                    {docs.map(d => (
                      <div key={d.id} className='py-2 flex items-center justify-between gap-4'>
                        <div className='min-w-0'>
                          <div className='font-medium text-gray-900 truncate'>{d.file.name}</div>
                          <div className='text-xs text-gray-500'>
                            {t('common.documentCategory')}: {categoryLabel(d.category)}
                          </div>
                        </div>
                        <Button
                          variant='destructive'
                          size='sm'
                          type='button'
                          onClick={() => setDocs(prev => prev.filter(x => x.id !== d.id))}
                        >
                          {t('common.remove')}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {stepIndex === 4 && (
              <div className='space-y-6'>
                <div className='bg-gray-50 border border-gray-200 rounded-xl p-4'>
                  <div className='font-semibold text-gray-900'>{t('common.review')}</div>
                  <div className='text-sm text-gray-700 mt-2 space-y-1'>
                    <div>
                      <span className='text-gray-600'>{t('common.name')}:</span> {core.firstName}{' '}
                      {core.lastName}
                    </div>
                    <div>
                      <span className='text-gray-600'>{t('common.phone')}:</span> {core.phoneNumber}
                    </div>
                    <div>
                      <span className='text-gray-600'>{t('common.email')}:</span>{' '}
                      {core.email || t('common.dash')}
                    </div>
                    <div>
                      <span className='text-gray-600'>{t('common.function')}:</span>{' '}
                      {core.function || t('common.dash')}
                    </div>
                    <div>
                      <span className='text-gray-600'>{t('common.documents')}:</span> {docs.length}
                    </div>
                  </div>
                </div>

                <FormTextarea
                  label={t('common.notes')}
                  name='notes'
                  value={extra.notes || ''}
                  onChange={e => setExtra(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder={t('users.notesPlaceholder')}
                />
              </div>
            )}

            <div className='flex items-center justify-between pt-6 border-t border-gray-200'>
              <Button type='button' variant='secondary' onClick={goBack} disabled={stepIndex === 0}>
                {t('common.back')}
              </Button>
              <div className='flex gap-3'>
                <Button type='button' variant='secondary' onClick={() => navigate('/users')}>
                  {t('common.cancel')}
                </Button>
                <Button
                  type='submit'
                  variant='primary'
                  loading={createUserMutation.isPending}
                  disabled={createUserMutation.isPending}
                >
                  {stepIndex === steps.length - 1
                    ? t('users.createEmployeeButton')
                    : t('common.next')}
                </Button>
              </div>
            </div>
          </div>
        </FormContainer>
      </Container>
    </PageLayout>
  )
}
